/**
 * Maps Deep Link Utilities
 * Opens device's native maps app without any API costs
 */

export const MapsProvider = {
  GOOGLE: 'google',
  APPLE: 'apple',
  WAZE: 'waze'
};

/**
 * Generate a deep link URL for the specified maps provider
 * @param {string} provider - The maps provider (google, apple, waze)
 * @param {object} location - { lat, lng, address }
 * @returns {string} The deep link URL
 *
 * Address-first strategy: We pass the typed address as a search query whenever
 * we have one, and only fall back to `lat,lng` when no text is available.
 * Reason: our address-autocomplete uses Nominatim (OpenStreetMap), which
 * frequently returns the centroid of the street as coords rather than the
 * actual house number — e.g. typing "554 Wunder St" returns coords pinned at
 * "540 Wunder St". Google / Apple / Waze have much better in-house geocoders
 * and will resolve the full address to the correct door.
 */
export const getMapsUrl = (provider, location) => {
  const { lat, lng, address } = location;
  const trimmedAddress = (address || "").trim();
  const encodedAddress = encodeURIComponent(trimmedAddress);
  // Treat 0,0 / null / undefined as "no coords" — without this we'd send users
  // to the middle of the Atlantic Ocean (0°N 0°W) and Apple/Waze return
  // "can't find the point" while Google opens a blank ocean map.
  const hasCoords =
    typeof lat === "number" && typeof lng === "number" &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    !(lat === 0 && lng === 0);
  const hasAddress = trimmedAddress.length > 0;

  switch (provider) {
    case MapsProvider.GOOGLE:
      // Native scheme (comgooglemaps://) launches the app with zero
      // browser-tab side effects. If Google Maps isn't installed the OS
      // will surface an "app not available" toast and the caller-side
      // fallback below reruns with the https:// URL.
      return hasAddress
        ? `comgooglemaps://?daddr=${encodedAddress}&directionsmode=driving`
        : hasCoords
          ? `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`
          : `comgooglemaps://`;

    case MapsProvider.APPLE:
      // Native maps:// scheme — iOS hands off directly to Apple Maps
      // without opening a new browser tab. This is what kills the
      // "swipe-back-to-blank-Edge" bug for good.
      return hasAddress
        ? `maps://?daddr=${encodedAddress}&dirflg=d&t=m`
        : hasCoords
          ? `maps://?daddr=${lat},${lng}&dirflg=d&t=m`
          : `maps://`;

    case MapsProvider.WAZE:
      // waze:// launches the Waze app. Fallback to https ll= if we only
      // have coords with no address text.
      return hasAddress
        ? `waze://?q=${encodedAddress}&navigate=yes`
        : hasCoords
          ? `waze://?ll=${lat},${lng}&navigate=yes`
          : `waze://`;

    default:
      return hasAddress
        ? `maps://?daddr=${encodedAddress}&dirflg=d&t=m`
        : hasCoords
          ? `maps://?daddr=${lat},${lng}&dirflg=d&t=m`
          : `maps://`;
  }
};

/**
 * HTTPS fallback URL — used when the native scheme fails to launch
 * (usually because the app isn't installed). Web viewer stays on the
 * device without hijacking the Link Ride tab.
 */
export const getMapsHttpsFallbackUrl = (provider, location) => {
  const { lat, lng, address } = location;
  const trimmedAddress = (address || "").trim();
  const encodedAddress = encodeURIComponent(trimmedAddress);
  const hasCoords =
    typeof lat === "number" && typeof lng === "number" &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    !(lat === 0 && lng === 0);
  const hasAddress = trimmedAddress.length > 0;

  switch (provider) {
    case MapsProvider.GOOGLE:
      return hasAddress
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`
        : hasCoords
          ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
          : `https://www.google.com/maps`;
    case MapsProvider.APPLE:
      return hasAddress
        ? `https://maps.apple.com/?daddr=${encodedAddress}&dirflg=d&t=m`
        : hasCoords
          ? `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d&t=m`
          : `https://maps.apple.com/`;
    case MapsProvider.WAZE:
      return hasAddress
        ? `https://waze.com/ul?q=${encodedAddress}&navigate=yes`
        : hasCoords
          ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
          : `https://waze.com/ul`;
    default:
      return hasAddress
        ? `https://maps.apple.com/?daddr=${encodedAddress}&dirflg=d&t=m`
        : `https://www.google.com/maps`;
  }
};

/**
 * Open maps with the given location
 * @param {string} provider - The maps provider
 * @param {object} location - { lat, lng, address }
 */
export const openMaps = (provider, location) => {
  const nativeUrl = getMapsUrl(provider, location);
  const httpsFallback = getMapsHttpsFallbackUrl(provider, location);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isEdge = /Edg\//i.test(ua);
  // Detect iOS reliably in 2026 (iPadOS 13+ reports as MacIntel with touch —
  // fall back on maxTouchPoints to catch it). This is the platform where
  // iframe URI-scheme launches were most broken.
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
    || (typeof navigator !== "undefined"
        && navigator.platform === "MacIntel"
        && (navigator.maxTouchPoints || 0) > 1);

  // Edge (desktop + Android) NEVER hands off cleanly from an iframe URI
  // scheme — it leaves a blank tab open and refuses to launch the target
  // app. Detect Edge and short-circuit straight to the HTTPS URL which
  // Edge handles cleanly.
  if (isEdge) {
    try {
      const a = document.createElement("a");
      a.href = httpsFallback;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      window.location.href = httpsFallback;
    }
    return;
  }

  // iOS 2024+ blocks iframe-based URI-scheme launches from PWAs (was
  // Apple's fix for scheme-based tracking). The iframe trick silently
  // fails → our 1.2s fallback then opens maps.apple.com in Safari, which
  // is exactly the "opens in browser instead of the app" complaint drivers
  // filed. The bulletproof replacement is a direct `location.href` fired
  // inside the user's click gesture: iOS hands off to the app immediately.
  // If the app isn't installed the request no-ops silently and we drop
  // through to the https universal-link fallback after a short window.
  if (isIOS) {
    let handedOff = false;
    const onVis = () => {
      if (document.visibilityState === "hidden") handedOff = true;
    };
    document.addEventListener("visibilitychange", onVis);
    try {
      // Direct assign — must be synchronous inside the click gesture, no
      // intermediate awaits, no iframe, no anchor click. This is what
      // gives us the native app hand-off on iOS.
      window.location.href = nativeUrl;
    } catch {
      // Scheme not registered / blocked — fall back immediately.
      handedOff = false;
    }
    setTimeout(() => {
      document.removeEventListener("visibilitychange", onVis);
      if (!handedOff) {
        // App isn't installed — universal-link the https URL. iOS Safari
        // will hand https://maps.apple.com/… to the Maps app if it IS
        // installed (universal-link handler), or open the web viewer
        // otherwise.
        window.location.href = httpsFallback;
      }
    }, 1400);
    return;
  }

  // Android + desktop non-Edge: iframe hand-off still works well and
  // keeps the Link Ride tab untouched. Preserving the original code path
  // here since drivers said "regular launch is fine."
  let handedOff = false;
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = nativeUrl;
  document.body.appendChild(iframe);

  const onVis = () => {
    if (document.visibilityState === "hidden") handedOff = true;
  };
  document.addEventListener("visibilitychange", onVis);

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVis);
    try { document.body.removeChild(iframe); } catch { /* already gone */ }
  };

  setTimeout(() => {
    cleanup();
    if (!handedOff) {
      try {
        const a = document.createElement("a");
        a.href = httpsFallback;
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        window.location.href = httpsFallback;
      }
    }
  }, 1200);
};

/**
 * Get display name for maps provider
 * @param {string} provider 
 * @returns {string}
 */
export const getMapsProviderName = (provider) => {
  switch (provider) {
    case MapsProvider.GOOGLE:
      return 'Google Maps';
    case MapsProvider.APPLE:
      return 'Apple Maps';
    case MapsProvider.WAZE:
      return 'Waze';
    default:
      return 'Maps';
  }
};

/**
 * Get icon name for maps provider (for lucide-react)
 * @param {string} provider 
 * @returns {string}
 */
export const getMapsProviderIcon = (provider) => {
  switch (provider) {
    case MapsProvider.GOOGLE:
      return 'map';
    case MapsProvider.APPLE:
      return 'apple';
    case MapsProvider.WAZE:
      return 'navigation';
    default:
      return 'map-pin';
  }
};
